import React from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, Chip, Alert, Stack, Avatar,
  useTheme, useMediaQuery
} from '@mui/material';
import {
  CheckCircle as CheckIcon, AutoFixHigh as FixIcon, School as SchoolIcon,
  Lightbulb as IdeaIcon, Edit as EditIcon
} from '@mui/icons-material';
import AddToVocabButton from './AddToVocabButton';

const JournalCorrectionsReview = ({ analysis, targetLanguage }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!analysis) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="text.secondary">
          No analysis data available for this entry.
        </Typography>
      </Box>
    );
  }

  const corrections = analysis.segments?.filter(
    s => s.type === 'vocabulary' || s.type === 'native' || s.type === 'grammar' || s.type === 'grammar_error' || s.correction
  ) || [];

  const vocabularyItems = corrections.filter(c => c.type === 'vocabulary' || c.type === 'native');
  const grammarItems = corrections.filter(c => c.type === 'grammar' || c.type === 'grammar_error');

  return (
    <Box>
      {/* Tutor Feedback */}
      {analysis.feedback && (
        <Paper
          elevation={0}
          sx={{
            p: isMobile ? 2 : 3,
            mb: 4,
            bgcolor: 'primary.50',
            border: '1px solid',
            borderColor: 'primary.100',
            borderRadius: 2
          }}
        >
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
              <SchoolIcon />
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold" color="primary.main" gutterBottom>
                Tutor Feedback
              </Typography>
              <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                {analysis.feedback}
              </Typography>
            </Box>
          </Stack>
        </Paper>
      )}

      {/* No corrections */}
      {corrections.length === 0 ? (
        <Alert
          severity="success"
          icon={<CheckIcon />}
          sx={{ borderRadius: 2 }}
        >
          <Typography variant="body1">
            Excellent work! Your entry was perfect - no corrections needed! 🎉
          </Typography>
        </Alert>
      ) : (
        <>
          {/* Vocabulary Learned Section */}
          {vocabularyItems.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                fontWeight="bold"
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
              >
                <IdeaIcon color="secondary" />
                New Vocabulary Learned
                <Chip label={vocabularyItems.length} size="small" color="secondary" />
              </Typography>

              <Grid container spacing={2}>
                {vocabularyItems.map((item, index) => (
                  <Grid item xs={12} key={index}>
                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        borderLeft: '4px solid',
                        borderColor: 'secondary.main',
                        bgcolor: 'secondary.50'
                      }}
                    >
                      <CardContent sx={{ p: isMobile ? 2 : 2.5 }}>
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} sm={5}>
                            <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block" gutterBottom>
                              YOU WROTE
                            </Typography>
                            <Typography variant="body1" sx={{ bgcolor: 'grey.100', p: 1, borderRadius: 1, display: 'inline-block' }}>
                              {item.text}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={7}>
                            <Typography variant="caption" color="secondary.main" fontWeight="bold" display="block" gutterBottom>
                              ✨ NOW YOU KNOW
                            </Typography>
                            <Typography variant="body1" fontWeight="bold" color="secondary.dark">
                              {item.correction}
                            </Typography>
                            {item.explanation && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                💡 {item.explanation}
                              </Typography>
                            )}
                            <Box sx={{ mt: 1.5 }}>
                              <AddToVocabButton
                                word={item.correction}
                                translation={item.text}
                                context={item.explanation}
                                languageCode={targetLanguage}
                                maxWords={3}
                              />
                            </Box>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Grammar Tips Section */}
          {grammarItems.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="h6"
                fontWeight="bold"
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}
              >
                <EditIcon color="primary" />
                Grammar Tips
                <Chip label={grammarItems.length} size="small" color="primary" />
              </Typography>

              <Grid container spacing={2}>
                {grammarItems.map((item, index) => (
                  <Grid item xs={12} key={index}>
                    <Card
                      variant="outlined"
                      sx={{
                        borderRadius: 2,
                        borderLeft: '4px solid',
                        borderColor: 'primary.main'
                      }}
                    >
                      <CardContent sx={{ p: isMobile ? 2 : 2.5 }}>
                        <Grid container spacing={2} alignItems="flex-start">
                          <Grid item xs={12} sm={5}>
                            <Typography variant="caption" color="text.secondary" fontWeight="bold" display="block" gutterBottom>
                              ORIGINAL
                            </Typography>
                            <Typography
                              variant="body1"
                              sx={{
                                textDecoration: 'line-through',
                                color: 'text.secondary',
                                bgcolor: 'grey.100',
                                p: 1,
                                borderRadius: 1,
                                display: 'inline-block'
                              }}
                            >
                              {item.text}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={7}>
                            <Typography variant="caption" color="success.main" fontWeight="bold" display="block" gutterBottom>
                              CORRECTED
                            </Typography>
                            <Typography variant="body1" fontWeight="bold">
                              {item.correction}
                            </Typography>
                            {item.explanation && (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  mt: 1.5,
                                  p: 1.5,
                                  bgcolor: 'grey.50',
                                  borderRadius: 1,
                                  borderLeft: '2px solid',
                                  borderColor: 'info.main'
                                }}
                              >
                                📝 {item.explanation}
                              </Typography>
                            )}
                            <Box sx={{ mt: 1.5 }}>
                              <AddToVocabButton
                                word={item.correction}
                                translation={item.text}
                                context={item.explanation}
                                languageCode={targetLanguage}
                                maxWords={3}
                              />
                            </Box>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </>
      )}

      {/* Summary stats */}
      {corrections.length > 0 && (
        <Paper
          variant="outlined"
          sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 2 }}
        >
          <Grid container spacing={2} textAlign="center">
            <Grid item xs={6}>
              <Typography variant="h4" color="secondary.main" fontWeight="bold">
                {vocabularyItems.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                New Words Learned
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="h4" color="primary.main" fontWeight="bold">
                {grammarItems.length}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Grammar Tips
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}
    </Box>
  );
};

export default JournalCorrectionsReview;